import NoteArea from "./NoteArea.js";
import InputNoteArea from "./InputNoteArea.js";
import Menu from "../Menu.js";
import Note from "../scene/drivers/Note.js";
import global from "../global.js";

const d3 = global.d3;

import Placeholder from "../scene/Placeholder.js";

const log = global.log.getLogger("f_mtac.ui.GraphicsArea");
log.setLevel(log.levels.WARN);

let GraphicsArea = function () {
    this.init = () => {
        const tools = global.tools;
        const scene = global.scene;

        const NOTE_CHAR_LIMIT = 32;
        const NOTE_LINE_LIMIT = 4;

        const soft_wrap = (s) =>
            String(s)
                .replace(/(?![^\n]{1,32}$)([^\n]{1,32})\s/g, "$1\n")
                .split("\n");
        const check_limits = (w) => {
            if (w.length > NOTE_LINE_LIMIT) {
                return false;
            }
            for (let l of w) {
                if (l.length > NOTE_CHAR_LIMIT) {
                    return false;
                }
            }
            return true;
        };

        const build_data = {
            active_object: null,
            active_driver: null,
            active_color: null
        };

        const driver_load = (name) => {
            if (!(name in scene.drivers)) {
                throw `scene driver ${name} doesn't exist`;
            }
            build_data.active_driver = scene.drivers[name];
        };

        const driver_unload = () => {
            build_data.active_driver = null;
        };
        const color_load = (color) => {
            build_data.active_color = color;
        };
        const color_unload = () => {
            build_data.active_color = null;
        };

        const graphis_placeholder = new Placeholder(
            scene.objects.layers.scene.group,
            "graphics",
            {
                start: (data, placeholder) => {
                    placeholder.move({x: d3.event.dx, y: d3.event.dy});
                    if (build_data.active_driver === null || build_data.active_color === null) {
                        return;
                    }
                    let {x, y} = placeholder.indicator_position;
                    data.active_object = new build_data.active_driver(scene.objects.layers.scene.group, scene.color);
                    data.active_object.draw(x, y);
                },
                drag: (data, placeholder) => {
                    Menu.unset_close_timeout(["graphics_tools_area"]);
                    placeholder.move({x: d3.event.dx, y: d3.event.dy});
                    if (build_data.active_driver === null || build_data.active_color === null) {
                        return;
                    }
                    let {x, y} = placeholder.indicator_position;
                    data.active_object.update(x, y);
                },
                end: (data, placeholder) => {
                    if (build_data.active_driver === null || build_data.active_color === null) {
                        return;
                    }
                    scene.objects.meta_create("scene", data.active_object);
                    data.active_object = null;
                    build_data.active_color = null;
                    placeholder.end();
                    Menu.close("graphics_tools_area");
                }
            },
            build_data
        );

        this.interaction = null;
        this.interact = () => {
            return new Promise((resolve) => {
                this.interaction = {
                    validate: () => {
                        resolve();
                        this.interaction = null;
                    },
                    cancel: () => {
                        resolve();
                        this.interaction = null;
                    }
                };
                Menu.display(["graphics_tools_area"]);
            });
        };

        Menu.create({
            name: "graphics_tools_area",
            icon: "add_graphics",
            contents: ["#graphics_tools_shape", "#graphics_tools_color"],
            open_handler: () => {
                /*
                scene.colors.forEach(color => {
                    if (color.value === scene.color) {
                        tools.groups.graphics_colors.select(`graphics_color_${color.name}`);
                    }
                });
                */
            },
            close_handler: () => {
                graphis_placeholder.end();
                tools.groups.graphics_colors.unselect();
                if (this.interaction !== null) {
                    this.interaction.cancel();
                }
            },
            close_timer: true
        });

        tools.register_group({
            name: "graphics_tools",
            target: "$view",
            enable: (_, selected) => {
                switch (selected.name) {
                    case "add_graphics":
                        break;
                    case "media":
                        global.medias.interact({}).then((runtime_data) => {
                            if (runtime_data === undefined) {
                                return;
                            }
                            global.scene.interaction_enable("drop_media");
                        });
                        break;
                    default:
                        scene.interaction_enable(selected.name);
                        break;
                }
            },
            disable: (_, selected) => {
                switch (selected.name) {
                    case "add_graphics":
                        break;
                    default:
                        scene.interaction_disable();
                        break;
                }
            }
        });

        tools.register({
            name: "add_graphics",
            icon: "add_graphics f_mtac-icon-xlg",
            position: "#command_graphics_area",
            data: {
                placeholder: graphis_placeholder
            },
            draggable: {
                start: (data, tool) => {
                    let {x: ox, y: oy} = tool.get_container_position();
                    data.origin = {
                        ox: 100 * scene.root_transform.k - ox,
                        oy: 100 * scene.root_transform.k - oy
                    };
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.init({
                        color: global.scene.map.grid.configuration.get("grid.color")
                    });
                    data.placeholder.move_to({x, y});
                },
                drag: (data) => {
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.move_to({x, y});
                },
                end: (data, tool) => {
                    if (tool.is_inside(data.placeholder.handle.node())) {
                        data.placeholder.end();
                        return Promise.resolve();
                    }
                    return this.interact();
                }
            }
        });

        tools.register({
            name: "add_note",
            icon: "add_post_it",
            position: "#command_graphics_area",
            enable: () => {},
            disable: () => {},
            data: {
                active_note: null,
                placeholder: new Placeholder(scene.objects.layers.notes.group, "note")
            },
            draggable: {
                start: (data, tool) => {
                    let {x: ox, y: oy} = tool.get_container_position();
                    data.origin = {
                        ox: 100 * scene.root_transform.k - ox,
                        oy: 100 * scene.root_transform.k - oy
                    };
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.init({
                        color: global.scene.map.grid.configuration.get("grid.color")
                    });
                    data.placeholder.move_to({x, y});
                },
                drag: (data) => {
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.move_to({x, y});
                },
                end: async (data, tool) => {
                    if (tool.is_inside(data.placeholder.handle.node())) {
                        data.placeholder.end();
                        return;
                    }
                    let color = await NoteArea.interact({});
                    if (color === null) {
                        data.active_note = null;
                        data.placeholder.end();
                        return;
                    }
                    let note_text = await InputNoteArea.interact({
                        tip: global.internationalization.text("postit.Text-of-the-note"),
                        validate_change: (new_text) => {
                            return check_limits(soft_wrap(new_text));
                        },
                        error_message: global.internationalization.text("postit.Text-shall-not-exceed-number-lines-and-number-characters"),
                        current_text: ""
                    });
                    if (note_text === null) {
                        data.active_note = null;
                        data.placeholder.end();
                        Menu.close("note_tools_area");
                        return;
                    }
                    note_text = soft_wrap(note_text);

                    let {x, y} = data.placeholder.indicator_position;
                    data.active_note = new Note(scene.objects.layers.notes.group, color, note_text);
                    data.active_note.draw(x, y);
                    scene.objects.meta_create("notes", data.active_note);
                    data.active_note = null;
                    data.placeholder.end();
                    Menu.close("note_tools_area");
                }
            }
        });

        tools.register({
            name: "object_remove",
            icon: "banc",
            position: "#command_graphics_area",
            data: {
                active_layer: null,
                active_object: null
            },
            draggable: {
                start: (data, tool) => {
                    let object_data = scene.objects_stack.pop();
                    if (object_data === undefined) {
                        data.active_object = null;
                        data.active_layer = null;
                        return;
                    }
                    data.active_object = object_data.object;
                    data.active_layer = object_data.layer;
                    let {x: ox, y: oy} = tool.get_container_position();
                    let object_handle = data.active_object.handle.select("circle.object_handle");
                    let hx = parseFloat(object_handle.attr("cx")) * scene.root_transform.k;
                    let hy = parseFloat(object_handle.attr("cy")) * scene.root_transform.k;
                    data.origin = {
                        ox: hx - ox,
                        oy: hy - oy
                    };
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    scene.objects.restore(data.active_layer, data.active_object);
                    data.active_object.transform({x, y});
                },
                drag: (data) => {
                    if (data.active_object === null) {
                        return;
                    }
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.active_object.transform({x, y});
                },
                end: (data, tool) => {
                    if (data.active_object === null) {
                        return Promise.resolve();
                    }
                    if (tool.is_inside(data.active_object.handle.select("circle.object_handle").node())) {
                        scene.objects_stack.push(data.active_object);
                        scene.objects.tx_delete(data.active_layer, data.active_object);
                        data.active_object = null;
                        data.active_layer = null;
                        return Promise.resolve();
                    }
                    scene.objects.publish(data.active_layer, data.active_object);
                    data.active_object = null;
                    data.active_layer = null;
                    return Promise.resolve();
                }
            }
        });

        tools.register_group({
            name: "graphics_draw",
            target: "$view",
            enable: (_, selected) => {
                if (selected.name !== "note") {
                    driver_load(selected.name);
                }
            },
            disable: (_, selected) => {
                if (selected.name !== "note") {
                    driver_unload();
                }
            }
        });

        tools.register({
            group: "graphics_draw",
            name: "line",
            icon: "draw_line",
            position: "#graphics_tools_shape"
        });
        tools.register({
            group: "graphics_draw",
            name: "cline",
            icon: "draw_curved_line",
            position: "#graphics_tools_shape"
        });
        tools.register({
            group: "graphics_draw",
            name: "disk",
            icon: "draw-circle",
            position: "#graphics_tools_shape"
        });
        tools.register({
            group: "graphics_draw",
            name: "polygon",
            icon: "draw-free",
            position: "#graphics_tools_shape"
        });
        tools.register({
            group: "graphics_draw",
            name: "arrow",
            icon: "draw_arrow",
            position: "#graphics_tools_shape"
        });
        tools.register({
            group: "graphics_draw",
            name: "carrow",
            icon: "draw_curved_arrow",
            position: "#graphics_tools_shape"
        });
        tools.register_group({
            name: "graphics_colors",
            target: "$none",
            enable: (_, selected) => {
                if (tools.groups.graphics_draw.selected === null) {
                    tools.groups.graphics_colors.unselect(selected.name);
                    return;
                }
                scene.color = selected.data.color.value;
                color_load(scene.color);
                /*if (tools.groups.graphics_draw.selected !== null && tools.groups.graphics_draw.selected.name === "note"){
                    note_interact();
                }*/
            },
            disable: () => {
                scene.color = null;
                color_unload();
            }
        });
        scene.colors.forEach((color) => {
            tools.register({
                group: "graphics_colors",
                name: `graphics_color_${color.name}`,
                data: {color},
                icon: "drop_black",
                position: "#graphics_tools_color",
                style: {color: color.value, "text-stroke": "1px #fff"}
            });
        });
        tools.groups.graphics_draw.select("line");
    };
    window.addEventListener("f_mtac.init", () => {
        this.init();
    });
};

export default new GraphicsArea();
